import { IDocument, ObjectValues } from '../common';

export const BILLING_CYCLE = {
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
} as const;

export type BillingCycle = (typeof BILLING_CYCLE)[keyof typeof BILLING_CYCLE];

export const BILLING_INTERVAL = {
  MONTH: 'month',
  YEAR: 'year',
} as const;

export type BillingInterval = (typeof BILLING_INTERVAL)[keyof typeof BILLING_INTERVAL];

export const PLAN_TYPE = {
  FREE: 'FREE',
  BASIC: 'BASIC',
  PREMIUM: 'PREMIUM',
  ENTERPRISE: 'ENTERPRISE',
  ADD_ON: 'ADD_ON',
} as const;

export type TPLAN_TYPE = ObjectValues<typeof PLAN_TYPE>;

export const FEATURE_NAMES = {
  EXPERT_CHATBOTS: 'expertChatbots',
  MESSAGE_CREDIT: 'messageCredit',
  TEAM_SEATS: 'teamSeats',
  KNOWLEDGE_BASE: 'knowledgeBase',
  URL_CRAWLS: 'urlCrawls',
  DOCUMENT_STUDIO: 'documentStudio',
  REMOVE_BRANDING: 'removeBranding',
  PRIVATE_CHATBOTS: 'privateChatbots',
  SUBDOMAIN_ALLOWED: 'subdomainAllowed',
  COMMUNITY_SUPPORT: 'communitySupport',
  EMAIL_SUPPORT: 'emailSupport',
  PRIORITY_EMAIL_SUPPORT: 'priorityEmailSupport',
  DEDICATED_MANAGER: 'dedicatedManager',
  MODELS_ALLOWED: 'modelsAllowed',
  CHATBOTS: 'chatBot',
  PRIVACY: 'privacy',
  BRANDING: 'branding',
  SUPPORT: 'support',
  KEY_FEATURES: 'keyFeatures',
  FILE_UPLOAD_LIMIT: 'fileUploadLimit',
  INSIGHT_TOKENS: 'insightTokens',
  TRANSLATION_TOKENS: 'translationTokens',
} as const;

export type TFEATURE_NAMES = (typeof FEATURE_NAMES)[keyof typeof FEATURE_NAMES];

export interface IFeature extends IDocument {
  name: TFEATURE_NAMES;
  description: null;
  quantity: number;
  quantityLabel?: string;
  isUnlimited: boolean;
  planId: string;
}

export interface Recurring {
  aggregate_usage: string | null;
  interval: BillingInterval;
  interval_count: number;
  meter: string | null;
  trial_period_days: string | null;
  usage_type: string;
}

export interface StripeProduct {
  id: string;
  object: string;
  active: boolean;
  attributes: string[];
  created: number;
  default_price: string;
  description: string;
  features: string[];
  images: string[];
  livemode: boolean;
  marketing_features: string[];
  metadata: string;
  name: string;
  package_dimensions: string | null;
  shippable: string | null;
  statement_descriptor: string | null;
  tax_code: string | null;
  type: string;
  unit_label: string | null;
  updated: number;
  url: string | null;
}

export interface StripePrice {
  id: string;
  object: string;
  active: boolean;
  billing_scheme: string;
  created: number;
  currency: string;
  custom_unit_amount: null;
  livemode: boolean;
  lookup_key: string | null;
  metadata: string;
  nickname: string;
  product: string;
  recurring: Recurring;
  tax_behavior: string;
  tiers_mode: string | null;
  transform_quantity: string | null;
  type: string;
  unit_amount: number;
  unit_amount_decimal: string;
}

export interface IStripe {
  product: StripeProduct;
  prices: StripePrice[];
}

export interface IPlanModal extends IDocument {
  modelId: string;
  planId: string;
  model: {
    name: string;
    isActive?: boolean;
    displayOrder?: number;
    createdAt?: string;
  };
}

export interface IPlan {
  id: string;
  name: string;
  type: TPLAN_TYPE;
  price: number;
  discountedPrice: number;
  billingCycle: string;
  defaultModelId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  features: IFeature[];
  internalId: string;
  stripe: IStripe | null;
  nickname?: string;
  priceId: string;
  stripePriceId?: string;
  stripePriceIdAnnual?: string;
  displayName?: string;
  modelPlans: IPlanModal[];
  // Billing provider (Stripe vs Shopify Billing)
  billingProvider?: 'STRIPE' | 'SHOPIFY';
  // Annual pricing fields (optional for backward compatibility)
  annualPrice?: number;
  annualDiscountedPrice?: number;
  pageData: {
    name?: string;
    description?: string;
    monthlyPrice?: string;
    yearlyPrice?: string;
    descriptionTagline?: string;
    features?: {
      key: string;
      value: string;
    }[];
  };
}

export interface AddOnCardDataProps {
  id: string;
  title: string;
  description: string;
  price: string;
  unit: string;
  benefit: string;
  quantityLabel: string;
}

export interface ICreateCheckoutSessionResponse {
  sessionId: string;
  sessionUrl: string;
}

export interface IUpdateSubscriptionResponse {
  portalSessionUrl: string;
}

export interface IStripeInvoice {
  id: string;
  subscriptionId: string;
  stripeInvoiceId: string;
  amount: number;
  currency: string;
  status: string;
  invoiceUrl: string;
  invoicePdf: string;
  paid: boolean;
  paidAt: string;
  createdAt: string;
  dueDate: string | null;
  periodStart: string;
  periodEnd: string;
  metadata: {
    stripeInvoiceData: {
      subtotal: number;
      total: number;
      amountPaid: number;
      amountDue: number;
      tax: number;
    };
  };
}

export interface IAddOn extends IDocument {
  name: string;
  quantity: number;
  currency: string;
  description: string;
  planId: string;
  price: number;
  priceId: string;
  productId: string;
  status: string;
  stripeInvoiceAmount: null;
  stripeInvoiceId: null;
  stripeInvoicePaid: null;
  subscriptionId: string;
  // Phase 8: Addon expiry tracking
  expiresAt?: string | null;
  statusChangedAt?: string | null;
}

export interface IActiveSubscription extends IDocument {
  id: string;
  userId: string;
  planId: string;
  status: string;
  stripeSubscriptionId: string;
  stripeCustomerId: string;
  stripePriceId: string;
  /**
   * Present when this subscription still bills a pre-repricing amount
   * (e.g. Starter grandfathered at $7 after the 2026-08-21 $20 repricing).
   * The UI must display THIS price for the current plan, not the Plan row's.
   */
  bundleBilling?: { amountCents: number; interval: 'month' | 'year'; currency: string };
  bundleBillingUnavailable?: boolean;
  grandfatheredPrice?: { price: number; interval: 'month' | 'year' };
  stripeProductId: string;
  quantity: number;
  startDate: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAt: string | null;
  canceledAt: string | null;
  endedAt: string | null;
  trialStart: string | null;
  trialEnd: string | null;
  paymentStatus: string;
  lastPaymentDate: string;
  nextPaymentDate: string | null;
  latestInvoiceId: string;
  latestInvoiceAmount: number;
  latestInvoicePaid: boolean;
  autoRenew: boolean;
  metadata: {
    userId: string;
    planId: string;
  };
  plan: IPlan;
  subscriptionInvoices: IStripeInvoice[];
  subscriptionAddons: IAddOn[];
  billingCycle: BillingInterval;
}

// Downgrade Preview Types
export interface IDowngradeChatbotInfo {
  id: string;
  name: string;
  createdAt: string;
  allocatedCredits: number;
  usedCredits: number;
  unusedCredits: number;
  resourceState: string;
  isSuggestedForFreeze: boolean;
}

export interface IDowngradePreviewResponse {
  currentPlan: {
    id: string;
    name: string;
    chatbotLimit: number;
    isUnlimited: boolean;
  };
  newPlan: {
    id: string;
    name: string;
    chatbotLimit: number;
    isUnlimited: boolean;
  };
  excessChatbotsCount: number;
  requiresSelection: boolean;
  chatbots: IDowngradeChatbotInfo[];
  totalCreditsToLiberate: number;
  proration: {
    amount: number;
    currency: string;
    effectiveDate: string;
  };
}

export interface ICreditLiberationResult {
  chatbotId: string;
  chatbotName: string;
  creditsLiberated: number;
}

export interface IExecuteDowngradeResponse {
  success: boolean;
  subscription: {
    id: string;
    planId: string;
    status: string;
  };
  creditsLiberated: {
    totalCreditsLiberated: number;
    chatbotResults: ICreditLiberationResult[];
  };
  frozenChatbots: Array<{
    id: string;
    name: string;
  }>;
  effectiveDate: string;
}

// Upgrade Preview Types (for unfreezing chatbots during upgrade)
export interface IFrozenChatbotInfo {
  id: string;
  name: string;
  frozenAt: string;
  frozenReason: string;
  daysUntilDeletion: number;
  deletionDate: string;
  canBeUnfrozen: boolean;
  createdFromAddonId: string | null;
  /** Also paused for private-expert capacity: unfreezing does not resume it. Optional for older API responses. */
  privateEntitlementHeld?: boolean;
  /** Present (false) only for paused private experts: nothing in the app resumes them yet. */
  canResume?: false;
}

/** A paused private expert. No plan change resumes it in the app yet. */
export interface IHeldPrivateExpertInfo {
  id: string;
  name: string;
  canResume: false;
}

export interface IUpgradePreviewResponse {
  currentPlan: {
    id: string;
    name: string;
    chatbotLimit: number;
    isUnlimited: boolean;
  };
  newPlan: {
    id: string;
    name: string;
    chatbotLimit: number;
    isUnlimited: boolean;
  };
  activeChatbotsCount: number;
  gainedChatbotSlots: number;
  hasFrozenChatbots: boolean;
  canUnfreezeChatbots: boolean;
  frozenChatbots: IFrozenChatbotInfo[];
  maxUnfreezeCount: number;
  /** Paused private experts this upgrade does NOT resume. Optional for older API responses. */
  heldPrivateExperts?: IHeldPrivateExpertInfo[];
  proration: {
    amount: number;
    currency: string;
    effectiveDate: string;
  };
}

export interface IExecuteUpgradeResponse {
  success: boolean;
  subscription: {
    id: string;
    planId: string;
    status: string;
  };
  unfrozenChatbots: Array<{
    id: string;
    name: string;
  }>;
  stillFrozenChatbots: Array<{
    id: string;
    name: string;
  }>;
  effectiveDate: string;
}
